import { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Typography, makeStyles } from '@material-ui/core';
import RichTextPreview from '../../../richTextPreview';
import SelectableUserList from '../../../selectableUserList';
import { prepareAnswerWeight } from '../../../../../utility/attribute';
import { isArray, isArrayEmpty } from '../../../../../utility/helpers';
import {
  isAttributeQuestionAnswerSelectedCheck,
  shouldRenderAttributeQuestionAnswerSubject,
} from '../../../../../utility/fillSurveyUtils';
import { NEUTRAL_ANSWER_WEIGHT } from '../../../questionEntry/config';

const useStyles = makeStyles(
  ({ palette: { primary }, spacing, breakpoints }) => ({
    root: {
      marginBottom: spacing(2),
      '&:last-of-type': {
        marginBottom: 0,
      },
    },
    rootAttributePreview: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: spacing(2),
      '&:last-of-type': {
        marginBottom: 0,
      },
    },
    answerContent: {
      boxSizing: 'border-box',
      border: `1px solid ${primary.bluish6}`,
      borderRadius: 8,
      display: 'flex',
      flexDirection: 'column',
      userSelect: 'none',
      '&.selected': {
        backgroundColor: primary.blue6,
        '& $answerOrder': {
          backgroundColor: primary.blue1,
          border: 'none',
          color: primary.white,
        },
        '&$singleSelectDisabled': {
          backgroundColor: primary.bluish8,
          '& $answerOrder': {
            backgroundColor: primary.bluish7,
          },
        },
      },
    },
    answerWrapper: {
      boxSizing: 'border-box',
      display: 'flex',
      alignItems: 'flex-start',
      minWidth: 0,
      padding: spacing(2),
      [breakpoints.up('sm')]: {
        padding: spacing(4),
      },
    },
    answerWrapperGroup: {
      padding: spacing(2, 2, 0, 2),
      [breakpoints.up('sm')]: {
        padding: spacing(4, 4, 0, 4),
      },
    },
    singleSelect: {
      cursor: 'pointer',
    },
    singleSelectDisabled: {
      cursor: 'default',
    },
    answerOrder: {
      boxSizing: 'border-box',
      border: `1px solid ${primary.bluish6}`,
      borderRadius: 4,
      fontSize: 12,
      lineHeight: '16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
      marginRight: spacing(1.5),
      height: 24,
      width: 24,
      [breakpoints.up('sm')]: {
        borderRadius: 8,
        fontSize: 16,
        lineHeight: '18px',
        height: 32,
        width: 32,
        marginRight: spacing(4),
      },
    },
    answerText: {
      flexGrow: 1,
      minWidth: 0,
      fontSize: 14,
      lineHeight: '16px',
      alignSelf: 'center',
      wordWrap: 'break-word',
      [breakpoints.up('sm')]: {
        fontSize: 16,
        lineHeight: '18px',
      },
    },
    weightLabel: {
      width: '91.5%',
    },
    answerWeight: {
      marginBottom: spacing(2),
      fontSize: 12,
      fontFamily: 'ProximaNova-Regular',
    },
  })
);

const VerticalAnswer = ({
  neutralAnswerLabel = '',
  subjects = [],
  questionId = 0,
  answer,
  selection = {},
  isDisabled = false,
  isSingleReview = false,
  isAttributePreview = false,
  isInsightsView = false,
  hasAddCommentButton = false,
  onSelect = () => {},
  onAddComment = () => {},
}) => {
  const classes = useStyles();

  const [subject] = subjects;
  const isSelected = isAttributeQuestionAnswerSelectedCheck(
    selection,
    answer.id,
    subject
  );
  const isGroupReview =
    !isSingleReview && isArray(subjects) && !isArrayEmpty(subjects);
  const answerWeight = prepareAnswerWeight(answer.weight);

  const handleSingleSelect = (answerId, user) => () => {
    if (isDisabled) return;

    onSelect(questionId, answerId, user, hasAddCommentButton);
  };

  const handleMultipleSelect = useCallback(
    (user, shouldDeleteAnswer) => {
      if (!isDisabled) {
        onSelect(
          questionId,
          answer.id,
          user,
          hasAddCommentButton,
          shouldDeleteAnswer
        );
      }
    },
    [isDisabled, questionId, answer.id, hasAddCommentButton, onSelect]
  );

  const isSubjectSelectedCheck = useCallback(
    user => isAttributeQuestionAnswerSelectedCheck(selection, answer.id, user),
    [selection, answer.id]
  );

  const isSubjectVisible = useCallback(
    user =>
      shouldRenderAttributeQuestionAnswerSubject(
        questionId,
        answer.id,
        selection,
        user
      ),
    [questionId, answer.id, selection]
  );

  return (
    <div
      className={classNames({
        [classes.root]: !isAttributePreview,
        [classes.rootAttributePreview]: isAttributePreview,
      })}
    >
      <div
        className={classNames(classes.answerContent, {
          [classes.singleSelect]: isSingleReview,
          [classes.singleSelectDisabled]: isDisabled,
          [classes.weightLabel]: isAttributePreview,
          selected: isSingleReview && isSelected,
        })}
        onClick={
          isSingleReview && !isSelected
            ? handleSingleSelect(answer.id, subject)
            : undefined
        }
      >
        <div
          className={classNames(classes.answerWrapper, {
            [classes.answerWrapperGroup]: isGroupReview,
          })}
        >
          <Typography
            className={classes.answerOrder}
            variant="subtitle1"
            component="div"
          >
            {answer.order <= 9 ? `0${answer.order}` : answer.order}
          </Typography>
          <RichTextPreview
            text={
              answerWeight === NEUTRAL_ANSWER_WEIGHT
                ? neutralAnswerLabel
                : answer.text
            }
            className={classes.answerText}
            variant="body1"
          />
        </div>
        {isGroupReview && (
          <SelectableUserList
            questionId={questionId}
            users={subjects}
            selection={selection}
            shouldRenderUser={isSubjectVisible}
            isDisabled={isDisabled}
            hasAddCommentButton={hasAddCommentButton}
            isInsightsView={isInsightsView}
            isUserSelectedCheck={isSubjectSelectedCheck}
            onSelect={handleMultipleSelect}
            onAddComment={onAddComment}
          />
        )}
      </div>
      {isAttributePreview && (
        <div className={classes.answerWeight}>
          {answerWeight !== NEUTRAL_ANSWER_WEIGHT
            ? prepareAnswerWeight(answer.weight)
            : ''}
        </div>
      )}
    </div>
  );
};

VerticalAnswer.propTypes = {
  answer: PropTypes.shape({}).isRequired,
  subjects: PropTypes.arrayOf(PropTypes.shape({})),
  questionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  neutralAnswerLabel: PropTypes.string,
  selection: PropTypes.shape({}),
  isSingleReview: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isAttributePreview: PropTypes.bool,
  isInsightsView: PropTypes.bool,
  onSelect: PropTypes.func,
  onAddComment: PropTypes.func,
  hasAddCommentButton: PropTypes.bool,
};

export default VerticalAnswer;
